import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ProductCard from "../components/ProductCard";
import Spinner from "../components/Spinner";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";

const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const INVENTORY_API = process.env.REACT_APP_INVENTORY_SERVICE_URL;
const REVIEWS_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

export default function ProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { auth, isAuthenticated } = useAuth();
  const { cart, addToCart, isInCart } = useCart();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: "",
    images: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [replying, setReplying] = useState({});

  const imgRef = useRef(null);
  const zoomRef = useRef(null);
  const [lensVisible, setLensVisible] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [bgPos, setBgPos] = useState("0% 0%");

  const getImageUrl = (imgMeta) =>
    imgMeta
      ? `https://auth.afrogebeya.com/uploads/products/${imgMeta.storedName.replace(
          /\\/g,
          "/",
        )}`
      : "https://via.placeholder.com/400";

  /* ---------------- FETCH PRODUCT ---------------- */
  useEffect(() => {
    let mounted = true;
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${PRODUCT_API}/products/${productId}`);
        if (!mounted) return;
        const prod = res.data;

        // inventory quantity
        try {
          const qtyRes = await axios.get(
            `${INVENTORY_API}/inventories/${productId}/quantity`,
          );
          prod.availableQuantity = Number(qtyRes.data);
        } catch {
          prod.availableQuantity = null;
        }

        setProduct(prod);
        setSelectedImage(getImageUrl(prod.mainImage || prod.images?.[0]));

        // related products
        if (prod.category?.id) {
          const relatedRes = await axios.get(`${PRODUCT_API}/products`, {
            params: { categoryId: prod.category.id, page: 0, size: 8 },
          });
          if (!mounted) return;
          setRelated(
            relatedRes.data.content.filter(
              (p) => p.productId !== prod.productId,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to fetch product", err);
      }
    };
    fetchProduct();
    return () => (mounted = false);
  }, [productId]);

  useEffect(() => {
    // Scroll to top whenever productId changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [productId]);

  /* ---------------- FETCH REVIEWS ---------------- */
  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${REVIEWS_API}/products/${productId}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setReviews(data);
    } catch (err) {
      console.error("Failed to fetch reviews", err);
    }
  }, [productId]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  /* ---------------- CART LOGIC ---------------- */
  const cartItem = cart.find((item) => item.productId === product?.productId);
  const maxReached =
    cartItem && product?.availableQuantity != null
      ? cartItem.quantity >= product.availableQuantity
      : false;
  const handleQuantityChange = (e) => {
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    const remaining =
      (product?.availableQuantity || 1) - (cartItem?.quantity || 0);
    setQuantity(Math.min(val, remaining));
  };
  const handleAddToCart = () => {
    if (product && quantity > 0) {
      addToCart(product, quantity);
      setQuantity(1);
    }
  };

  /* ---------------- ZOOM & LENS ---------------- */
  const handleMouseMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xPercent = (x / rect.width) * 100;
    const yPercent = (y / rect.height) * 100;
    setLensPos({ x, y });
    setBgPos(`${xPercent}% ${yPercent}%`);
    if (zoomRef.current)
      zoomRef.current.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
  };

  /* ---------------- REVIEW SUBMISSION ---------------- */
  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) return alert("Login required.");
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("productId", productId);
      formData.append("rating", newReview.rating);
      formData.append("comment", newReview.comment);
      newReview.images.forEach((file) => formData.append("images", file));

      await axios.post(`${REVIEWS_API}`, formData, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setNewReview({ rating: 5, comment: "", images: [] });
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to submit review.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------- SELLER REPLY ---------------- */
  const handleReplySubmit = async (reviewId) => {
    const text = replying[reviewId]?.trim();
    if (!text) return alert("Reply cannot be empty");
    try {
      await axios.post(
        `${REVIEWS_API}/${reviewId}/reply`,
        { comment: text },
        { headers: { Authorization: `Bearer ${auth.token}` } },
      );
      setReplying((prev) => ({ ...prev, [reviewId]: "" }));
      fetchReviews();
    } catch (err) {
      console.error(err);
      alert("Failed to submit reply.");
    }
  };

  /* ---------------- RATING SUMMARY ---------------- */
  const ratingCounts = [5, 4, 3, 2, 1].map(
    (star) => reviews.filter((r) => r.rating === star).length,
  );
  const averageRating =
    reviews.length > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
      : 0;

  if (!product)
    return (
      <div className="container mt-4">
        <Spinner />
      </div>
    );

  const formattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: product.currency || "USD",
  }).format(product.price || 0);
  const isSeller = auth?.roles?.includes("SELLER");

  return (
    <div className="container mt-4">
      <div className="row g-4">
        {/* Product Images */}
        <div className="col-lg-6 d-flex flex-column flex-md-row gap-3">
          <div className="d-flex flex-column gap-2" style={{ maxWidth: 100 }}>
            {product.mainImage && (
              <img
                src={getImageUrl(product.mainImage)}
                alt="thumb"
                className={`img-thumbnail ${
                  selectedImage === getImageUrl(product.mainImage)
                    ? "border-primary"
                    : ""
                }`}
                style={{ width: 80, height: 80, objectFit: "cover" }}
                onClick={() => setSelectedImage(getImageUrl(product.mainImage))}
              />
            )}
            {product.images?.map((img, idx) => (
              <img
                key={idx}
                src={getImageUrl(img)}
                alt={`thumb-${idx}`}
                className={`img-thumbnail ${
                  selectedImage === getImageUrl(img) ? "border-primary" : ""
                }`}
                style={{ width: 80, height: 80, objectFit: "cover" }}
                onClick={() => setSelectedImage(getImageUrl(img))}
              />
            ))}
          </div>
          <div className="position-relative flex-grow-1 text-center">
            <img
              ref={imgRef}
              src={selectedImage}
              alt={product.productName}
              className="img-fluid rounded border"
              onMouseEnter={() =>
                window.innerWidth > 768 && setLensVisible(true)
              }
              onMouseLeave={() => setLensVisible(false)}
              onMouseMove={(e) => window.innerWidth > 768 && handleMouseMove(e)}
              style={{ maxHeight: 400, objectFit: "contain", width: "100%" }}
            />
            {lensVisible && (
              <>
                <div
                  className="lens-rect"
                  style={{
                    left: lensPos.x - 75,
                    top: lensPos.y - 50,
                    backgroundImage: `url(${selectedImage})`,
                    backgroundPosition: bgPos,
                  }}
                />
                <div
                  ref={zoomRef}
                  className="zoom-box"
                  style={{ backgroundImage: `url(${selectedImage})` }}
                />
              </>
            )}
          </div>
        </div>

        {/* Product Info */}
        <div className="col-lg-6">
          <h3>{product.productName}</h3>
          <p className="text-muted">{product.description}</p>
          <p>
            <strong>Brand:</strong> {product.brand || "NA"}
          </p>
          <p>
            <strong>Model:</strong> {product.modelNumber || "NA"}
          </p>
          <p className="fs-4 fw-bold">{formattedPrice}</p>
          <p>
            <strong>Available:</strong>{" "}
            {product.availableQuantity != null
              ? product.availableQuantity
              : "N/A"}
          </p>

          <div className="d-flex align-items-center gap-2 my-3 flex-wrap">
            <label htmlFor="quantityInput" className="mb-0">
              Qty:
            </label>
            <input
              id="quantityInput"
              type="number"
              min="1"
              max={product.availableQuantity ?? 1}
              value={quantity}
              onChange={handleQuantityChange}
              className="form-control form-control-sm"
              style={{ width: 80 }}
              disabled={maxReached}
            />
            {maxReached && (
              <span className="text-danger small">
                Max quantity reached in cart
              </span>
            )}
          </div>
          <button
            className="btn btn-primary mb-3"
            onClick={handleAddToCart}
            disabled={maxReached}
          >
            🛒{" "}
            {maxReached
              ? "Max in Cart"
              : isInCart(product.productId)
                ? "Add More"
                : "Add to Cart"}
          </button>
        </div>
      </div>

      {/* Related Products */}
      <div className="mt-5">
        <h5>Related Products</h5>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3">
          {related.length ? (
            related.map((rp) => (
              <div
                className="col"
                key={rp.productId}
                onClick={() => navigate(`/products/${rp.productId}`)}
                style={{ cursor: "pointer" }}
              >
                <ProductCard product={rp} />
              </div>
            ))
          ) : (
            <p>No related products found.</p>
          )}
        </div>
      </div>

      {/* Rating Summary */}
      <div className="mt-5">
        <h5>Rating Summary</h5>
        <p>Average Rating: {averageRating.toFixed(1)} / 5</p>
        {[5, 4, 3, 2, 1].map((star, idx) => {
          const count = ratingCounts[idx];
          const percent = reviews.length ? (count / reviews.length) * 100 : 0;
          return (
            <div key={star} className="mb-1 d-flex align-items-center gap-2">
              <span style={{ width: 35 }}>{star}⭐</span>
              <div
                style={{
                  flex: 1,
                  height: 10,
                  background: "#eee",
                  borderRadius: 5,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    background: "#0d6efd",
                  }}
                ></div>
              </div>
              <span style={{ width: 30, textAlign: "right" }}>{count}</span>
            </div>
          );
        })}
      </div>

      {/* Reviews Section */}
      <div className="mt-5">
        <h5>Reviews ({reviews.length})</h5>
        {reviews.map((r) => (
          <div key={r.id} className="border p-2 mb-2 rounded">
            <strong>{r.email}</strong> - {"⭐".repeat(r.rating)}
            <p>{r.comment}</p>
            {r.images?.length > 0 && (
              <div className="d-flex gap-2 mb-2">
                {r.images.map((img) => (
                  <img
                    key={img.id}
                    src={`https://auth.afrogebeya.com/uploads/products/${img.storedName}`}
                    alt="review-img"
                    style={{ width: 60, height: 60, objectFit: "cover" }}
                  />
                ))}
              </div>
            )}
            <small>{new Date(r.createdAt).toLocaleString()}</small>
            {r.reply && (
              <div className="border-start ps-2 mt-1">
                <strong>Seller Reply:</strong> <p>{r.reply}</p>
              </div>
            )}
            {isSeller && !r.reply && (
              <div className="mt-2">
                <textarea
                  placeholder="Write a reply..."
                  className="form-control mb-1"
                  value={replying[r.id] || ""}
                  onChange={(e) =>
                    setReplying((prev) => ({ ...prev, [r.id]: e.target.value }))
                  }
                />
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleReplySubmit(r.id)}
                >
                  Reply
                </button>
              </div>
            )}
          </div>
        ))}

        {/* Submit new review */}
        {isAuthenticated && !isSeller && (
          <form onSubmit={handleReviewSubmit} className="mt-3">
            <h6>Submit a Review</h6>
            <div className="mb-2">
              <label className="d-block mb-1">Rating:</label>
              <div style={{ display: "flex", gap: 5 }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{
                      fontSize: 24,
                      cursor: "pointer",
                      color: newReview.rating >= star ? "#ffc107" : "#ddd",
                    }}
                    onClick={() =>
                      setNewReview((prev) => ({ ...prev, rating: star }))
                    }
                  >
                    ⭐
                  </span>
                ))}
              </div>
            </div>
            <textarea
              className="form-control mb-2"
              rows={3}
              placeholder="Write your review..."
              value={newReview.comment}
              onChange={(e) =>
                setNewReview((prev) => ({ ...prev, comment: e.target.value }))
              }
            />
            <label>Upload images (max 3)</label>
            <input
              type="file"
              accept="image/*"
              multiple
              className="form-control mb-2"
              onChange={(e) =>
                setNewReview((prev) => ({
                  ...prev,
                  images: Array.from(e.target.files).slice(0, 3),
                }))
              }
            />
            <button className="btn btn-success" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        )}
      </div>

      {/* Zoom CSS */}
      <style>{`
        .lens-rect {
          position: absolute;
          width: 150px;
          height: 100px;
          border: 2px solid rgba(0,0,0,0.25);
          background-repeat: no-repeat;
          background-size: 200%;
          pointer-events: none;
          z-index: 10;
          transition: background-position 0.05s ease;
        }
        .zoom-box {
          position: absolute;
          top: 0;
          right: -410px;
          width: 400px;
          height: 400px;
          border: 1px solid #ddd;
          background-repeat: no-repeat;
          background-size: 200%;
          z-index: 10;
          pointer-events: none;
        }
        @media (max-width: 768px) { .lens-rect, .zoom-box { display: none !important; } }
      `}</style>
    </div>
  );
}
